import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { MultiSelectFilter } from '../../../../components/ui/MultiSelectFilter';
import type { PortalCampaignOption } from '../../../../types/portalReport';
import { PortalShell } from '../PortalShell';
import { PeriodControl } from './PeriodControl';
import { SelectField } from '../../../../components/ui/SelectField';
import { PORTAL_COMPARISON_MODES } from '../../../../lib/portalReport/comparison';
import type { ReportState } from './useReportState';

// CLIENT PORTAL Phase 1 — the report's shared frame: the three page tabs, the
// period control, the location drill-down breadcrumb, and (on the channel pages)
// the campaign filter.

const TABS = [
  { to: '/portal/report', label: 'Overview', end: true },
  { to: '/portal/report/google', label: 'Google Ads', end: false },
  { to: '/portal/report/meta', label: 'Meta', end: false },
];

export function ReportLayout({
  state,
  signedInAs,
  accounts,
  selectedAccountId,
  campaignOptions,
  showsGoogle = true,
  children,
}: {
  state: ReportState;
  signedInAs: string | null;
  accounts: Array<{ id: string; name: string }>;
  selectedAccountId: string | null;
  /** Absent on the Overview — the campaign filter is a channel-page control. */
  campaignOptions?: PortalCampaignOption[];
  /**
   * ⚠️⚠️ THE PRE-RESTYLE REPORT IS STILL LIVE for any account whose restyle flag
   * is false, so "any mention of Google" has to hold on whichever report the
   * client gets. False drops the Google Ads tab.
   *
   * ⚠️ Defaults TRUE so the many existing call sites are unchanged; every page
   * that has the payload passes it explicitly.
   */
  showsGoogle?: boolean;
  children: ReactNode;
}) {
  const selected = accounts.find((a) => a.id === selectedAccountId) ?? null;
  // ⚠️ FILTERED, not branched inside the `.map()` — see the restyle header's nav
  // for the same reasoning.
  const tabs = TABS.filter((t) => showsGoogle || t.to !== '/portal/report/google');

  return (
    <PortalShell signedInAs={signedInAs}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Your report</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {selected
              ? selected.name
              : accounts.length === 1
                ? accounts[0]?.name
                : `${accounts.length} locations`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PeriodControl
            preset={state.preset}
            custom={state.custom}
            onPresetChange={state.setPreset}
            onCustomChange={state.setCustom}
          />
          {/*
            ⚠️ Names the MODE, not the dates. The window is resolved server-side
            through the AM Advertising Home's own comparisonRange, so a client
            and their account manager can never end up comparing different
            windows under the same words.
          */}
          <SelectField
            aria-label="Compare with"
            value={state.comparison}
            onValueChange={(v) => state.setComparison(v as typeof state.comparison)}
            options={PORTAL_COMPARISON_MODES.map((m) => ({
              value: m.value,
              label: `vs ${m.label.toLowerCase()}`,
            }))}
          />
        </div>
      </div>

      <nav className="mb-6 flex gap-1 border-b border-border" aria-label="Report sections">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              isActive
                ? '-mb-px border-b-2 px-3 py-2 text-sm font-medium text-foreground'
                : '-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground'
            }
            // ⚠️ ONE OF THE FOUR BRAND SURFACES. The underline takes the brand
            // accent; the LABEL keeps kyck's ink, because a brand-coloured word
            // on a brand-coloured rule is where accenting turns into skinning.
            // Falls through to the kyck default (slate-900) when unthemed.
            style={({ isActive }) =>
              isActive ? { borderBottomColor: 'var(--portal-accent)' } : undefined
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {selected && (
        // ⚠️ A drill-down must be visibly escapable. Without this the client is
        // looking at one location's figures with no sign that the rest exist.
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            Showing <span className="font-medium text-foreground">{selected.name}</span> only.
          </span>
          <Button variant="ghost" size="sm" onClick={() => state.setAccountId(null)}>
            Show all locations
          </Button>
        </div>
      )}

      {campaignOptions && campaignOptions.length > 0 && (
        <CampaignFilter
          options={campaignOptions}
          selected={state.campaignIds}
          onChange={state.setCampaignIds}
        />
      )}

      {children}
    </PortalShell>
  );
}

/**
 * The campaign filter. ⚠️ It narrows the WHOLE page — the KPIs, both charts and
 * both tables — because a filter that only moved the table would leave the
 * figures above it describing a different set of campaigns.
 *
 * ⚠️⚠️ ROUND 2 SWAPPED THE CHIP ROW FOR THE SHARED `MultiSelectFilter`, and that
 * REVERSED a Phase-1 UI decision. The chips treated "untick everything" as an
 * EXPLICIT EMPTY filter (show nothing); this control's convention — the one
 * every staff dashboard uses — is that an empty selection means NO CONSTRAINT.
 *
 * The reversal is deliberate. Keeping the old meaning inside this control would
 * have made its own trigger lie: it renders "All campaigns" when nothing is
 * ticked, and a trigger saying "All" above a page showing none is worse than
 * either convention on its own. Reusing a shared control means adopting its
 * semantics.
 *
 * ⚠️ THE SERVER-SIDE DISTINCTION IS UNTOUCHED. `campaignIds: []` still means
 * "filtered to nothing" and short-circuits to an empty result; only the UI no
 * longer produces that state. That guarantee is security-adjacent — it is what
 * stops a tampered empty filter reading as "no constraint" — so it stays, and
 * its tests stay.
 */
function CampaignFilter({
  options,
  selected,
  onChange,
}: {
  options: PortalCampaignOption[];
  selected: string[] | undefined;
  onChange: (ids: string[] | undefined) => void;
}) {
  return (
    <div className="mb-6">
      <MultiSelectFilter
        label="Campaigns"
        ariaLabel="Filter by campaign"
        placeholder="All campaigns"
        // Only the campaigns present in the selected period — the vocabulary is
        // resolved UNFILTERED server-side, so picking one never erases the rest.
        options={options.map((o) => ({ value: o.campaignId, label: o.campaignName }))}
        selected={selected ?? []}
        onChange={(next) => onChange(next.length === 0 ? undefined : next)}
      />
    </div>
  );
}
